import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Package, CheckCircle } from 'lucide-react';
import { apiClient } from '../../services/authService';
import { showInfo, showError } from '../../utils/toastUtils';
import './OrderConfirmation.css';

const OrderConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invoiceDetails, setInvoiceDetails] = useState(null);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPaymentStatusColor = () => {
    switch (orderDetails?.orderStatus?.toLowerCase()) {
      case 'placed':
      case 'success':
        return '#16a34a';
      case 'pending':
        return '#d97706';
      case 'failed':
        return '#dc2626';
      default:
        return '#64748b';
    }
  };

  const getPaymentStatusBg = () => {
    switch (orderDetails?.orderStatus?.toLowerCase()) {
      case 'placed':
      case 'success':
        return 'rgba(22, 163, 74, 0.1)';
      case 'pending':
        return 'rgba(217, 119, 6, 0.1)';
      case 'failed':
        return 'rgba(220, 38, 38, 0.1)';
      default:
        return 'rgba(100, 116, 139, 0.1)';
    }
  };

  const handleViewAllOrders = () => {
    navigate('/orders');
  };

  useEffect(() => {
    const fetchOrderDetails = async (orderId) => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        if (!token) {
          showInfo('Please log in to view order details', {
            autoClose: 5000,
            onClick: () => navigate('/login'),
          });
          return;
        }

        const response = await apiClient.get(`/user/orders/${orderId}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (response.status === 200 && response.data.status === 'success') {
          setOrderDetails(response.data.data);
        } else {
          showError('Failed to fetch order details', { autoClose: false });
        }
      } catch (error) {
        console.error('Error fetching order details:', error);
        showError('Error fetching order details. Please try again.', { autoClose: false });
      } finally {
        setLoading(false);
      }
    };

    const fetchInvoiceDetails = async (invoiceId) => {
      try {
        const token = localStorage.getItem('token');
        const response = await apiClient.get(`/user/invoice/${invoiceId}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });


        if (response.status === 200 && response.data.status === 'success') {
          setInvoiceDetails(response.data.data);
        } else {
          console.warn('Invoice fetch failed or not ready:', response.data?.message || 'No invoice data');
          showError('Invoice not available yet. Please check later or contact support.', { autoClose: false });
        }
      } catch (error) {
        console.error('Error auto-fetching invoice:', error);
        showError('Failed to load invoice details. Please try again later.', { autoClose: false });
      }
    };

    const orderId = location.state?.orderId || new URLSearchParams(location.search).get('orderId');
    const invoiceId = location.state?.invoiceId; // Use invoiceId from payment flow
    if (orderId) {
      fetchOrderDetails(orderId);
      if (invoiceId) {
        fetchInvoiceDetails(invoiceId);
      } else {
        console.warn('No invoiceId provided, invoice may not be available yet.');
        showError('Invoice ID not available yet. Please check later.', { autoClose: false });
      }
    } else {
      setLoading(false);
      showError('Order ID not found. Please check your order history.', { autoClose: false });
    }
  }, [location, navigate]);

  if (loading) {
    return (
      <div className="order-confirmation-page">
        <div className="order-confirmation-container">
          <div className="order-details-card">
            <div className="card-content">
              <p>Loading order details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!orderDetails) {
    return (
      <div className="order-confirmation-page">
        <div className="order-confirmation-container">
          <div className="order-details-card">
            <div className="card-content">
              <p>Order not found. Please check your order history.</p>
              <button onClick={() => navigate('/')} className="action-btn">
                Go to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="order-confirmation-page">
      <div className="order-confirmation-container">
        <div className="order-details-card">
          <div className="card-header">
            <div className="header-icon">
              <Package size={24} />
            </div>
            <div className="header-text">
              <h3 className="card-title">Order Confirmed</h3>
              <p className="card-subtitle">
                <CheckCircle className="tick-icon" size={18} />
                Thank you for choosing Kapil Agro
              </p>
            </div>
          </div>

          <div className="card-content">
            <div className="detail-row">
              <span className="detail-label">Order ID</span>
              <span className="detail-value detail-value-small">{orderDetails.orderId}</span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Invoice ID</span>
              <span className="detail-value detail-value-small">
                {invoiceDetails?.invoiceId || 'Pending'}
              </span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Order Date</span>
              <span className="detail-value">{formatDate(orderDetails.placedAt || new Date())}</span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Payment Method</span>
              <span className="detail-value">{orderDetails.paymentMethod || 'Online Payment'}</span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Payment Status</span>
              <span
                className="payment-status-badge"
                style={{ color: getPaymentStatusColor(), backgroundColor: getPaymentStatusBg() }}
              >
                {orderDetails.orderStatus || 'Pending'}
              </span>
            </div>
          </div>

          <div className="action-buttons">
            <button onClick={handleViewAllOrders} className="action-btn">
              <Package size={20} />
              View All My Orders
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;